import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import moment from "moment";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { UnanetQueueDetailModal } from "@components/business/BusinessIntegrations/UnanetConnect/UnanetSyncStatus/UnanetQueueDetailModal";
import UnanetService from "@services/UnanetService";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import IOrder from "@models/IOrder";
import { IUnanetOrderSyncStatusResponse } from "@models/Unanet/IUnanetDashboardQueryParams";
import "./UnanetOrderSyncStatus.scss";

type Props = {
    order: IOrder;
};

type DisplayState = "not_enabled" | "not_mapped" | "not_queued" | "pending" | "processing" | "synced" | "failed" | "resolved";

const deriveDisplayState = (status: IUnanetOrderSyncStatusResponse): DisplayState => {
    if (!status.hasUnanet) return "not_enabled";
    if (!status.vendorMapped) return "not_mapped";

    const q = status.queueEntry;
    if (q) {
        if (q.status === "failed") return "failed";
        if (q.status === "resolved") return "resolved";
        if (q.status === "processing") return "processing";
        if (q.status === "pending") return "pending";
        if (q.status === "success") return "synced";
        return "pending"; // safe fallback for unknown statuses
    }

    if (status.order.unanet_journal_entry_key !== null) return "synced";
    return "not_queued";
};

const BADGE_CONFIG: Record<DisplayState, { title: string; bgColor: string; icon: string }> = {
    not_enabled: { title: "N/A", bgColor: "bg-secondary", icon: "" },
    not_mapped: { title: "not_synced", bgColor: "bg-secondary", icon: "" },
    not_queued: { title: "not_synced", bgColor: "bg-secondary", icon: "" },
    pending: { title: "pending", bgColor: "bg-warning", icon: "fas fa-clock" },
    processing: { title: "processing", bgColor: "bg-info", icon: "fas fa-spinner fa-spin" },
    synced: { title: "synced", bgColor: "bg-success", icon: "fas fa-check-circle" },
    failed: { title: "failed", bgColor: "bg-danger", icon: "fas fa-times-circle" },
    resolved: { title: "resolved", bgColor: "bg-secondary", icon: "fas fa-check" },
};

export const UnanetOrderSyncStatus = ({ order }: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [syncStatus, setSyncStatus] = useState<IUnanetOrderSyncStatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [resolutionNote, setResolutionNote] = useState("");
    const [showResolveInput, setShowResolveInput] = useState(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedSyncLogGuid, setSelectedSyncLogGuid] = useState<string | null>(null);

    const fetchSyncStatus = useCallback(async () => {
        try {
            const data = await UnanetService.getOrderSyncStatus(order.guid, userModel, supplierModel);
            setSyncStatus(data);
        } catch (e) {
            console.error("Error fetching order sync status:", e);
        } finally {
            setLoading(false);
        }
    }, [order.guid, userModel, supplierModel]);

    useEffect(() => {
        fetchSyncStatus();
    }, [fetchSyncStatus]);

    const handleSyncNow = async () => {
        setActionLoading(true);
        try {
            await UnanetService.queueJournalEntry({ orderGuid: order.guid }, userModel, supplierModel);
            await UnanetService.processQueue(userModel, supplierModel);
            uiContext.showNotification({
                message: "unanet_sync_success",
                level: NotificationLevelEnum.success,
            });
            await fetchSyncStatus();
        } catch (e) {
            console.error("Error syncing order:", e);
            uiContext.showNotification({
                message: "unanet_sync_error",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRetry = async () => {
        if (!syncStatus?.queueEntry) return;
        setActionLoading(true);
        try {
            await UnanetService.retryQueueItem(syncStatus.queueEntry.guid, userModel, supplierModel);
            await UnanetService.processQueue(userModel, supplierModel);
            uiContext.showNotification({
                message: "unanet_retry_success",
                level: NotificationLevelEnum.success,
            });
            await fetchSyncStatus();
        } catch (e) {
            console.error("Error retrying queue item:", e);
            uiContext.showNotification({
                message: "unanet_sync_error",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleShowDetails = () => {
        const syncLogGuid = syncStatus?.queueEntry?.sync_log_guid;
        if (!syncLogGuid) return;
        setSelectedSyncLogGuid(syncLogGuid);
        setDetailModalOpen(true);
    };

    const closeDetailModal = (refresh?: boolean) => {
        setDetailModalOpen(false);
        setSelectedSyncLogGuid(null);
        if (refresh) fetchSyncStatus();
    };

    const handleResolve = async () => {
        if (!syncStatus?.queueEntry) return;
        setActionLoading(true);
        try {
            await UnanetService.resolveQueueItem(
                syncStatus.queueEntry.guid,
                { resolution_note: resolutionNote || undefined },
                userModel,
                supplierModel
            );
            uiContext.showNotification({
                message: "unanet_resolve_success",
                level: NotificationLevelEnum.success,
            });
            setResolutionNote("");
            setShowResolveInput(false);
            await fetchSyncStatus();
        } catch (e) {
            console.error("Error resolving queue item:", e);
            uiContext.showNotification({
                message: "unanet_sync_error",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="UnanetOrderSyncStatus p-3">
                <i className="fas fa-circle-notch fa-spin me-2" />
                <OwLabel title="loading" />
            </div>
        );
    }

    if (!syncStatus) {
        return (
            <div className="UnanetOrderSyncStatus p-3">
                <OwLabel title="unanet_sync_error" />
            </div>
        );
    }

    const displayState = deriveDisplayState(syncStatus);
    const badge = BADGE_CONFIG[displayState];

    const renderErrorMessages = (errorMessage: string | null) => {
        if (!errorMessage) return <span>—</span>;
        const parts = errorMessage
            .split("|")
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length <= 1) return <span>{errorMessage}</span>;
        return (
            <ul className="UnanetOrderSyncStatus__error-list">
                {parts.map((msg, i) => (
                    <li key={i}>{msg}</li>
                ))}
            </ul>
        );
    };

    const renderStateContent = () => {
        switch (displayState) {
            case "not_enabled":
                return (
                    <div className="UnanetOrderSyncStatus__message">
                        <OwLabel title="unanet_not_enabled" />
                    </div>
                );

            case "not_mapped":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__message">
                            <OwLabel title="unanet_not_mapped" />
                        </div>
                        <div className="UnanetOrderSyncStatus__actions">
                            <Link to="/business/integrations/unanet/project-mapping" className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-link me-2" />
                                <OwLabel title="unanet_map_project" />
                            </Link>
                        </div>
                    </>
                );

            case "not_queued":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__message">
                            <OwLabel title="unanet_not_queued" />
                        </div>
                        <div className="UnanetOrderSyncStatus__actions">
                            <OwButton
                                title="unanet_sync_now"
                                className="btn-primary"
                                leftIconClass="fas fa-sync-alt"
                                fn={handleSyncNow}
                                active={actionLoading}
                                disableOnActive={true}
                            />
                        </div>
                    </>
                );

            case "pending":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__message">
                            <OwLabel title="unanet_queued_for_sync" />
                        </div>
                        <div className="UnanetOrderSyncStatus__actions">
                            <OwButton
                                title="unanet_sync_now"
                                className="btn-primary"
                                leftIconClass="fas fa-sync-alt"
                                fn={handleSyncNow}
                                active={actionLoading}
                                disableOnActive={true}
                            />
                        </div>
                    </>
                );

            case "processing":
                return (
                    <div className="UnanetOrderSyncStatus__message">
                        <i className="fas fa-spinner fa-spin me-2" />
                        <OwLabel title="unanet_sync_in_progress" />
                    </div>
                );

            case "synced":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__info-grid">
                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_journal_entry" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value UnanetOrderSyncStatus__info-value--highlight">
                                {syncStatus.order.unanet_document_number || "—"}
                            </span>

                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_synced_at" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value">
                                <i className="far fa-calendar-alt me-2" />
                                {syncStatus.order.unanet_synced_at
                                    ? moment(syncStatus.order.unanet_synced_at).format("M/D/YY, h:mm A")
                                    : "—"}
                            </span>
                        </div>
                        {syncStatus.queueEntry?.sync_log_guid && (
                            <div className="UnanetOrderSyncStatus__actions">
                                <OwButton
                                    title="details"
                                    className="btn-outline-secondary"
                                    leftIconClass="fas fa-info-circle"
                                    fn={handleShowDetails}
                                />
                            </div>
                        )}
                    </>
                );

            case "failed":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__info-grid">
                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_error" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value UnanetOrderSyncStatus__error-text">
                                {renderErrorMessages(syncStatus.queueEntry?.error_message ?? null)}
                            </span>

                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_last_attempt" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value">
                                {syncStatus.queueEntry?.processed_at
                                    ? moment(syncStatus.queueEntry.processed_at).format("M/D/YY, h:mm A")
                                    : "—"}
                            </span>
                        </div>
                        <div className="UnanetOrderSyncStatus__actions">
                            <OwButton
                                title="retry"
                                className="btn-warning"
                                leftIconClass="fas fa-redo"
                                fn={handleRetry}
                                active={actionLoading}
                                disableOnActive={true}
                            />
                            {syncStatus.queueEntry?.sync_log_guid && (
                                <OwButton
                                    title="details"
                                    className="btn-outline-secondary"
                                    leftIconClass="fas fa-info-circle"
                                    fn={handleShowDetails}
                                />
                            )}
                            {!showResolveInput ? (
                                <OwButton
                                    title="resolve"
                                    className="btn-secondary"
                                    leftIconClass="fas fa-check"
                                    fn={() => setShowResolveInput(true)}
                                    disabled={actionLoading}
                                />
                            ) : (
                                <div className="UnanetOrderSyncStatus__resolve-section">
                                    <div className="UnanetOrderSyncStatus__resolve-input">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={resolutionNote}
                                            onChange={(e) => setResolutionNote(e.target.value)}
                                            placeholder={intl.formatMessage({
                                                id: "unanet_resolution_note",
                                                defaultMessage: "Resolution Note",
                                            })}
                                        />
                                    </div>
                                    <div className="UnanetOrderSyncStatus__resolve-buttons">
                                        <OwButton
                                            title="mark_resolved"
                                            className="btn-secondary"
                                            leftIconClass="fas fa-check"
                                            fn={handleResolve}
                                            active={actionLoading}
                                            disableOnActive={true}
                                        />
                                        <OwButton
                                            title="cancel"
                                            className="btn-outline-secondary"
                                            fn={() => {
                                                setShowResolveInput(false);
                                                setResolutionNote("");
                                            }}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                );

            case "resolved":
                return (
                    <>
                        <div className="UnanetOrderSyncStatus__info-grid">
                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_resolution_note" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value">{syncStatus.queueEntry?.resolution_note || "—"}</span>

                            <span className="UnanetOrderSyncStatus__info-label">
                                <OwLabel title="unanet_resolved_at" noBottomMargin />
                            </span>
                            <span className="UnanetOrderSyncStatus__info-value">
                                {syncStatus.queueEntry?.resolved_at
                                    ? moment(syncStatus.queueEntry.resolved_at).format("M/D/YY, h:mm A")
                                    : "—"}
                            </span>
                        </div>
                        <div className="UnanetOrderSyncStatus__actions">
                            <OwButton
                                title="retry"
                                className="btn-warning"
                                leftIconClass="fas fa-redo"
                                fn={handleRetry}
                                active={actionLoading}
                                disableOnActive={true}
                            />
                            {syncStatus.queueEntry?.sync_log_guid && (
                                <OwButton
                                    title="details"
                                    className="btn-outline-secondary"
                                    leftIconClass="fas fa-info-circle"
                                    fn={handleShowDetails}
                                />
                            )}
                        </div>
                    </>
                );
        }
    };

    return (
        <div className="UnanetOrderSyncStatus p-3">
            <div className="UnanetOrderSyncStatus__header">
                <OwLabel title="unanet_integration" className="UnanetOrderSyncStatus__title" />
                <OwBadge title={badge.title} bgColor={badge.bgColor} leftIconClass={badge.icon || undefined} />
            </div>
            {renderStateContent()}
            {selectedSyncLogGuid && (
                <UnanetQueueDetailModal syncLogGuid={selectedSyncLogGuid} isOpen={detailModalOpen} onClose={closeDetailModal} />
            )}
        </div>
    );
};
